"use client";

import { useSyncExternalStore } from "react";

/**
 * Reveals heavy child content one macro-task after the current paint, so the component
 * renders its skeleton first and flips on the next task (keeps hydration unblocked).
 *
 * SHARED SIGNAL: a single module-level `setTimeout(0)` drives ALL consumers. Previously
 * every instance scheduled its own timer — with ~100 product-box tails that meant ~100
 * timers + ~100 `setState`s firing as one burst on the next macro-task (main-thread
 * stress). Now one timer flips a shared flag and notifies every subscriber in one batch.
 *
 * `setTimeout(0)` (not `requestIdleCallback`) because rIC can be starved for seconds on a
 * busy low-tier Android main thread.
 *
 * HYDRATION: read via `useSyncExternalStore`, not `useState(globalRevealed)`. The shared
 * timer can fire while React is still hydrating later subtrees, which made them render the
 * revealed content against server HTML that still held the skeleton (#418).
 */
let globalRevealed = false;
let scheduled = false;
const listeners = new Set<() => void>();

function scheduleReveal() {
  if (typeof window === "undefined" || globalRevealed || scheduled) return;
  scheduled = true;
  setTimeout(() => {
    globalRevealed = true;
    listeners.forEach((l) => l());
  }, 0);
}

const subscribe = (onStoreChange: () => void) => {
  listeners.add(onStoreChange);
  scheduleReveal();
  return () => {
    listeners.delete(onStoreChange);
  };
};

const getSnapshot = () => globalRevealed;
const getServerSnapshot = () => false;

export function useIdleReveal(immediate = false) {
  const isRevealed = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );

  // `immediate` comes from a server-provided prop (above-the-fold priority), so it is
  // identical on both sides and safe to short-circuit with.
  return immediate || isRevealed;
}
